import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import { Dictionary } from '@reduxjs/toolkit';
import { useBoolean } from 'ahooks';
import { Col, Row, Space } from 'antd';
import { isEmpty } from 'lodash';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { RoleType, UserType } from 'api/models';
import { UserMeta } from 'api/user';
import { ReactComponent as Astronaut } from 'assets/svgs/astro.svg';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Modal } from 'components/atoms/modal';
import { Radio } from 'components/atoms/radio';
import { CreatableSelect, Select } from 'components/atoms/select';
import { Tag } from 'components/atoms/tag';
import {
  BodyLong01,
  Caption01,
  ProductiveHeading05,
  ProductiveHeading06,
  Text03,
} from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import {
  useUpdateCurrentUserMutation,
  useUpdateUserMetaMutation,
} from 'queries';
import { useUpdateHubspotMutation } from 'queries/hubspot';
import { useCreateRoleMutation, useVendorRolesQuery } from 'queries/roles';
import { byLabel } from 'utils/comparators/by-label';

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding: 0 !important;
  }
`;

const modalMaskCSS = {
  zIndex: 1011,
};

const Title = styled.div`
  ${ProductiveHeading05};
`;

const Description = styled.div`
  ${BodyLong01};
`;

const WelcomeNote = styled(Col)`
  background-color: var(--gray-10);
`;

const PaddedContainer = styled.div`
  padding: var(--spacing-08) var(--spacing-06);
`;

const SpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  height: 100%;
`;

const SvgContainer = styled.div`
  display: flex;
`;

const FormContainer = styled.div`
  width: 360px;
  padding: var(--spacing-07) var(--spacing-07) 0 var(--spacing-07);
`;

const Heading = styled.h6`
  ${ProductiveHeading06};
  margin-bottom: var(--layout-02);
`;

const Footer = styled.div`
  padding: var(--spacing-05);
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: flex-end;
`;

const Hint = styled.div`
  ${Caption01};
  ${Text03};
`;

const ModalHeightCSS = css`
  height: 380px;
`;

const VideoContainer = styled(Col)`
  ${ModalHeightCSS};
`;

const Iframe = styled.iframe`
  ${ModalHeightCSS};
  width: 100%;
`;

const CloseButtonContainer = styled.div`
  position: absolute;
  right: 0;
  top: -40px;
`;

const ModalCloseButton = styled(Tag)<{ disabled: boolean }>`
  background-color: var(--gray-80);
  color: var(--white-100);
  border: 2px solid transparent;
  cursor: pointer;
  ${({ disabled }) =>
    disabled
      ? css`
          pointer-events: none;
        `
      : ''};
`;

type Opt = { label: string; value: string };

const convertFormValuesToPayload = (
  values: Dictionary<string | number | Opt>,
): Dictionary<string | number> => {
  return Object.entries(values).reduce((obj, entry) => {
    const [key, value] = entry;

    return {
      ...obj,
      [key]: typeof value === 'object' ? value.value : value,
    };
  }, {});
};

const IntroVideo = ({ firstName, onClose }) => {
  const { mutate, isLoading } = useUpdateUserMetaMutation();
  const handleClose = () => {
    mutate({ metaData: { profileSetupDone: true } });
    onClose();
  };

  return (
    <>
      <CloseButtonContainer>
        <ModalCloseButton onClick={handleClose} disabled={isLoading}>
          <FormattedMessage id="close" />
        </ModalCloseButton>
      </CloseButtonContainer>
      <Row gutter={0}>
        <WelcomeNote span={8}>
          <PaddedContainer>
            <Space direction="vertical" size={32}>
              <Title>
                <FormattedMessage
                  id="intro_modal.title"
                  values={{
                    firstName,
                    b: (chunks) => <b>{chunks}</b>,
                  }}
                />
              </Title>
              <Description>
                <Space direction="vertical" size={24}>
                  <FormattedMessage id="intro_modal.message.1" />
                  <FormattedMessage id="intro_modal.message.2" />
                </Space>
              </Description>
            </Space>
          </PaddedContainer>
        </WelcomeNote>
        <VideoContainer span={16}>
          <Iframe
            src="https://www.youtube.com/embed/Md0b2WaAUj4"
            title="Rocketlane Overview"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </VideoContainer>
      </Row>
    </>
  );
};

const IntroModalContent = ({ firstName, userType, originalRole, onFinish }) => {
  const { data: roles, isLoading: isRolesLoading } = useVendorRolesQuery();
  const { mutateAsync: updateMeta, isLoading: isUpdatingMeta } =
    useUpdateUserMetaMutation();
  const { mutateAsync: updateHubspot, isLoading: isUpdatingHubspot } =
    useUpdateHubspotMutation();
  const { mutateAsync: updateCurrentUser, isLoading: isUpdatingUser } =
    useUpdateCurrentUserMutation();
  const { mutateAsync: createRole, isLoading: isCreatingRole } =
    useCreateRoleMutation();

  const [form] = Form.useForm();

  const validationRequiredText = useTranslation(
    'intro_modal.form.role.validation.required',
  );

  const handleSubmit = async (values) => {
    const { role, ...rest } = values;
    let newlyCreatedRole;

    if (role.__isNew__) {
      newlyCreatedRole = await createRole({
        roleName: role.value,
        use_as_placeholder: false,
        type: RoleType.Vendor,
      });
    }
    await updateCurrentUser(
      {
        role: {
          roleId: newlyCreatedRole ? newlyCreatedRole.roleId : role.value,
          roleName: newlyCreatedRole ? newlyCreatedRole.roleName : role.label,
          type: newlyCreatedRole ? newlyCreatedRole.type : RoleType.Vendor,
        },
      },
      {
        onSuccess: async () => {
          await updateHubspot(
            { user: convertFormValuesToPayload(rest) },
            {
              onSuccess: async () => {
                await updateMeta(
                  { metaData: { profileSetupDone: true } },
                  { onSuccess: () => onFinish() },
                );
              },
            },
          );
        },
      },
    );
  };

  const rolesOptions = useMemo(() => {
    return (
      userType === UserType.Partner
        ? (roles ?? []).filter(({ usedByPartners }) => usedByPartners === true)
        : roles ?? []
    )
      .map((role) => ({
        label: role.roleName,
        value: role.roleId,
      }))
      .sort(byLabel);
  }, [roles, userType]);

  const isLoading =
    isCreatingRole || isUpdatingHubspot || isUpdatingMeta || isUpdatingUser;

  return (
    <>
      {isEmpty(originalRole) || isLoading ? (
        <Row gutter={0}>
          <WelcomeNote flex="246px">
            <SpaceBetween>
              <PaddedContainer>
                <Space direction="vertical" size={32}>
                  <Title>
                    <FormattedMessage
                      id="intro_modal.title"
                      values={{
                        firstName,
                        b: (chunks) => <b>{chunks}</b>,
                      }}
                    />
                  </Title>
                  <Description>
                    <Space direction="vertical" size={24}>
                      <FormattedMessage id="intro_modal.message.1" />
                    </Space>
                  </Description>
                </Space>
              </PaddedContainer>
              <SvgContainer>
                <Astronaut />
              </SvgContainer>
            </SpaceBetween>
          </WelcomeNote>
          <Col flex="auto">
            <FormContainer>
              <Heading>
                <FormattedMessage id="intro_modal.form.heading" />
              </Heading>
              <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Form.Item
                  label={<FormattedMessage id="intro_modal.form.role.label" />}
                  name="role"
                  rules={[
                    {
                      required: true,
                      message: validationRequiredText,
                    },
                  ]}
                >
                  <CreatableSelect
                    closeMenuOnSelect
                    isClearable
                    isLoading={isRolesLoading}
                    options={rolesOptions}
                    isValidNewOption={(option) =>
                      option.length > 0 && userType !== UserType.Partner
                    }
                    placeholder={
                      userType === UserType.Partner ? (
                        <FormattedMessage id="settings.team_members.invite.placeholder.role_choose_only" />
                      ) : (
                        <FormattedMessage id="settings.team_members.invite.placeholder.role" />
                      )
                    }
                  />
                </Form.Item>
                <Form.Item
                  name="team_size"
                  label={
                    <FormattedMessage id="intro_modal.form.team_size.label" />
                  }
                >
                  <Select
                    options={[
                      { label: ' 1 to 10', value: '1-10' },
                      { label: ' 11 to 20', value: '11-20' },
                      { label: ' 21 to 50', value: '21-50' },
                      { label: ' 50+', value: '50+' },
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  name="project_type"
                  label={<FormattedMessage id="intro_modal.form.type.label" />}
                >
                  <Radio.Group>
                    <Space direction="vertical">
                      <Radio value="customer_facing">
                        <Space direction="vertical" size={2}>
                          <div>
                            <FormattedMessage id="intro_modal.form.type.customer" />
                          </div>
                          <Hint>
                            <FormattedMessage id="intro_modal.form.type.customer.help" />
                          </Hint>
                        </Space>
                      </Radio>
                      <Radio value="internal_and_customer_facing">
                        <Space direction="vertical" size={2}>
                          <div>
                            <FormattedMessage id="intro_modal.form.type.both" />
                          </div>
                          <Hint>
                            <FormattedMessage id="intro_modal.form.type.both.help" />
                          </Hint>
                        </Space>
                      </Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
              </Form>
            </FormContainer>
            <Footer>
              <Button
                type="primary"
                iconPlacement="right"
                icon={<ArrowRight16 />}
                onClick={() => form.submit()}
                loading={isLoading}
              >
                <FormattedMessage id="intro_modal.form.button" />
              </Button>
            </Footer>
          </Col>
        </Row>
      ) : (
        <IntroVideo firstName={firstName} onClose={onFinish} />
      )}
    </>
  );
};

export const IntroModal: FC = () => {
  const {
    user: { firstName, role, userType },
    userMeta = {} as UserMeta,
  } = useUser();
  const { metaData } = userMeta;
  const [visible, { setFalse: closeModal }] = useBoolean(
    !metaData?.profileSetupDone,
  );

  return (
    <StyledModal
      width={812}
      visible={visible}
      closable={false}
      footer={null}
      centered
      maskStyle={modalMaskCSS}
      zIndex={1012}
      destroyOnClose
    >
      <IntroModalContent
        firstName={firstName}
        userType={userType}
        originalRole={role}
        onFinish={closeModal}
      />
    </StyledModal>
  );
};
